// ABOUTME: Modal for finding an existing scope by level and name/ID.
// ABOUTME: Two-stage flow: input → verify → found / not found. Opens scope on selection.
import {
  Button,
  ControlledInput,
  DropdownItems,
  DropdownListItem,
  Icon,
  Modal,
  ModalFooter,
  ModalTitle,
  Pill,
  TextSemiBold,
  TextSmallRegular
} from '@hm/fundamental'
import {mdiCheck, mdiChevronDown, mdiClose, mdiLoading, mdiMagnify} from '@mdi/js'
import React, {useEffect, useMemo, useRef, useState} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {ScopeLevel, ScopeResultItem, VerifyScopeResult} from '../types'
import {SCOPE_LEVEL_OPTIONS} from '../utils'
import {ScopeLevelIcon} from './ScopeLevelIcon'

export interface FindScopeModalProps {
  open: boolean
  onClose: () => void
  /**
   * Called when the user clicks Verify. Should resolve to a VerifyScopeResult.
   * TODO: Replace with real API call when endpoint is available.
   */
  onVerify: (level: ScopeLevel, scopeValue: string) => Promise<VerifyScopeResult>
  /** Called when user selects a scope and clicks "Open this scope" */
  onOpenScope?: (scope: ScopeResultItem) => void
}

type Stage = 'input' | 'verifying' | 'found' | 'notFound'

export const FindScopeModal = ({
  open,
  onClose,
  onVerify,
  onOpenScope
}: FindScopeModalProps) => {
  const {t} = useTranslation()
  const [level, setLevel] = useState<ScopeLevel | null>(null)
  const [scopeValue, setScopeValue] = useState('')
  const [stage, setStage] = useState<Stage>('input')
  const [results, setResults] = useState<ScopeResultItem[]>([])
  const [selectedResultIndex, setSelectedResultIndex] = useState<number | null>(null)
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false)
  const levelDropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelledRef = useRef(false)

  const sortedResults = useMemo(
    () => [...results].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [results]
  )

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setLevel(null)
      setScopeValue('')
      setStage('input')
      setResults([])
      setSelectedResultIndex(null)
      setLevelDropdownOpen(false)
      cancelledRef.current = true
    }
  }, [open])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(e.target as Node)) {
        setLevelDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const resetResults = () => {
    setStage('input')
    setResults([])
    setSelectedResultIndex(null)
  }

  const handleLevelSelect = (value: ScopeLevel) => {
    setLevel(value)
    setLevelDropdownOpen(false)
    if (stage === 'found' || stage === 'notFound') resetResults()
  }

  const clearLevel = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLevel(null)
    setScopeValue('')
    if (stage === 'found' || stage === 'notFound') resetResults()
  }

  const handleVerify = async () => {
    if (!scopeValue.trim() || !level) return
    cancelledRef.current = false
    resetResults()
    setStage('verifying')
    try {
      const result = await onVerify(level, scopeValue.trim())
      if (cancelledRef.current) return
      if (result.found && result.results && result.results.length > 0) {
        setResults(result.results)
        setStage('found')
      } else {
        setStage('notFound')
      }
    } catch {
      if (!cancelledRef.current) {
        setStage('notFound')
      }
    }
  }

  const handleAction = () => {
    if (selectedResultIndex === null) return
    const selected = sortedResults[selectedResultIndex]
    if (!selected) return
    onOpenScope?.(selected)
    onClose()
  }

  const selectedOption = level ? SCOPE_LEVEL_OPTIONS.find((o) => o.value === level) : null
  const levelLabel = selectedOption
    ? t(selectedOption.labelKey)
    : t('countryConfigurations.entity.findScopeModal.levelPlaceholder')

  const isVerifying = stage === 'verifying'
  const isFound = stage === 'found'
  const isNotFound = stage === 'notFound'
  const canVerify = scopeValue.trim().length > 0 && level !== null && !isVerifying
  const canAction = isFound && selectedResultIndex !== null

  const actionLabel = t('countryConfigurations.entity.findScopeModal.openThisScope')

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>{t('countryConfigurations.entity.findScopeModal.title')}</ModalTitle>

      <div className="pt-6 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
        {/* Level dropdown (full-width, input-styled) */}
        <div ref={levelDropdownRef} className="relative w-full">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setLevelDropdownOpen((o) => !o)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setLevelDropdownOpen((o) => !o)}
            aria-expanded={levelDropdownOpen}
            aria-haspopup="listbox"
            className="w-full h-12 flex items-center justify-between px-3 rounded-lg border border-border_highlight_10 bg-white text-left cursor-pointer"
          >
            <div className="flex flex-col items-start flex-1 min-w-0">
              {level !== null ? (
                <>
                  <span className="text-xs text-foreground_faded leading-none">
                    {t('countryConfigurations.entity.findScopeModal.levelPlaceholder')}
                  </span>
                  <div className="flex items-center gap-1 mt-0.5">
                    {selectedOption && <Icon path={selectedOption.icon} size="regular" className={selectedOption.iconClassName} />}
                    <TextSmallRegular className="text-foreground">{levelLabel}</TextSmallRegular>
                  </div>
                </>
              ) : (
                <span className="text-base text-foreground_faded">
                  {t('countryConfigurations.entity.findScopeModal.levelPlaceholder')}
                </span>
              )}
            </div>
            <div className="flex items-center self-stretch shrink-0">
              <Icon path={mdiChevronDown} size="regular" />
              {level !== null && (
                <>
                  <div className="w-px self-stretch bg-border_highlight_10 ml-2 mr-1" />
                  <Button variant="tertiary" type="button" onClick={clearLevel} aria-label="Clear" icon={mdiClose} size="extraSmall" className="!h-auto !p-0 !pl-2 !rounded-none !text-foreground hover:!bg-transparent active:!bg-transparent" />
                </>
              )}
            </div>
          </div>
          <DropdownItems isOpen={levelDropdownOpen} width={180} className="top-full left-0 mt-1 !max-w-none">
            {SCOPE_LEVEL_OPTIONS.map((opt, idx) => (
              <DropdownListItem
                key={opt.value}
                title={t(opt.labelKey)}
                icon={opt.icon}
                iconClassName={opt.iconClassName}
                onClick={() => handleLevelSelect(opt.value)}
                isSelected={level === opt.value}
                className={
                  idx < SCOPE_LEVEL_OPTIONS.length - 1
                    ? 'relative !border-b-0 after:absolute after:bottom-0 after:left-4 after:right-4 after:h-px after:bg-border'
                    : '!border-b-0'
                }
              />
            ))}
          </DropdownItems>
        </div>

        {/* Name or ID input */}
        <div className={`relative w-full ${level === null ? 'opacity-50' : ''}`}>
          <ControlledInput
            ref={inputRef}
            value={scopeValue}
            isDisabled={level === null}
            onChange={(e) => {
              setScopeValue(e.target.value)
              if (stage === 'found' || stage === 'notFound') resetResults()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && stage === 'input' && scopeValue.trim()) {
                void handleVerify()
              }
            }}
            placeholder={t('countryConfigurations.entity.findScopeModal.nameOrIdPlaceholder')}
            variant="regular"
            className="w-full"
          />
          <div className="absolute right-0 top-0 bottom-0 flex items-center self-stretch shrink-0">
            <Icon path={mdiMagnify} size="regular" />
            {scopeValue && (
              <>
                <div className="w-px self-stretch bg-border_highlight_10 ml-2 mr-1" />
                <Button
                  variant="tertiary"
                  type="button"
                  aria-label="Clear"
                  icon={mdiClose}
                  size="extraSmall"
                  className="!h-auto !p-0 !pl-2 !pr-3 !rounded-none !text-foreground hover:!bg-transparent active:!bg-transparent"
                  onClick={() => {
                    setLevel(null)
                    setScopeValue('')
                    if (stage === 'found' || stage === 'notFound') resetResults()
                    inputRef.current?.focus()
                  }}
                />
              </>
            )}
            {!scopeValue && <span className="pr-3" />}
          </div>
        </div>
        </div>

        {/* Result area */}
         {isFound && sortedResults.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            <TextSmallRegular className="text-foreground_faded">
              {t('countryConfigurations.entity.findScopeModal.resultsCount', {count: sortedResults.length})}
            </TextSmallRegular>
            <div className="flex flex-col w-full max-h-[288px] overflow-auto rounded-lg border border-border [scrollbar-width:thin] [scrollbar-color:#d6d3d1_#fafafa] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-50 [&::-webkit-scrollbar-track]:border-l [&::-webkit-scrollbar-track]:border-gray-200 [&::-webkit-scrollbar-track]:mt-1 [&::-webkit-scrollbar-track]:mb-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-300 [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:[background-clip:padding-box]">
              {sortedResults.map((item, index) => (
                  <div
                    key={item.id ?? index}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedResultIndex(index)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedResultIndex(index)}
                    className={[
                      'relative flex items-center gap-2 w-full px-4 py-2 cursor-pointer',
                      selectedResultIndex === index
                        ? 'bg-container_background_selected hover:bg-container_background_selected'
                        : 'hover:bg-container_background_hover',
                      index < sortedResults.length - 1
                        ? 'after:absolute after:bottom-0 after:left-4 after:right-4 after:h-px after:bg-border'
                        : ''
                    ].join(' ')}
                  >
                    <ScopeLevelIcon level={item.level} />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-base font-normal text-foreground leading-6">{item.displayName}</span>
                      {(item.level || item.displayNumber) && (
                        <span className="text-xs text-foreground_faded leading-4">
                          {[item.level ? t(SCOPE_LEVEL_OPTIONS.find(o => o.value === item.level)?.labelKey ?? '') : null, item.displayNumber].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </div>
                    {item.isOverridden && (
                      <Pill type="primary" variant="shade" text={t('countryConfigurations.entity.findScopeModal.overridden')} size="regular" className="shrink-0" />
                    )}
                    {selectedResultIndex === index && (
                      <Icon path={mdiCheck} size="regular" color="var(--primary)" className="shrink-0" />
                    )}
                  </div>
              ))}
            </div>
          </div>
        )}
        {isNotFound && (
          <div className="flex flex-col gap-1">
            <TextSemiBold>
              {t('countryConfigurations.entity.findScopeModal.noResultFound')}
            </TextSemiBold>
            <TextSmallRegular className="text-foreground">
              {t('countryConfigurations.entity.findScopeModal.noResultHint')}
            </TextSmallRegular>
          </div>
        )}
      </div>

      <ModalFooter justifyClass="justify-start">
        <Button variant="secondary" type="button" onClick={onClose} className="flex-1">
          {t('countryConfigurations.entity.findScopeModal.cancel')}
        </Button>

        {!isFound ? (
          <Button
            variant="primary"
            type="button"
            disabled={!canVerify}
            onClick={() => void handleVerify()}
            className="flex-1"
            iconPrefix={
              isVerifying ? (
                <Icon path={mdiLoading} size="regular" className="animate-spin" />
              ) : (
                <Icon path={mdiMagnify} size="regular" />
              )
            }
          >
            {!isVerifying && t('countryConfigurations.entity.findScopeModal.verify')}
          </Button>
        ) : (
          <Button
            variant="primary"
            type="button"
            disabled={!canAction}
            onClick={handleAction}
            className="flex-1"
          >
            {actionLabel}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}
